import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { isBusy, onBusyChange } from '@/lib/busy';
import { hasActivity, installActivityTracking, routeLeft } from './activity';
import { APP_VERSION, fetchDeployedVersion, watcherEnabled } from './app-version';
import { installBrowserPreloadRecovery } from './preload-recovery';
import { updateServiceWorker, waitForNewWorker } from './sw-registry';
import { attachVersionTriggers, createVersionMonitor, type VersionMonitor } from './version-monitor';

/**
 * Moves the open app onto the latest deployment (INTERFACES section 8).
 * Rendered once inside the router; takes no props. Each check also asks the
 * service worker to update, and a reload first waits for a new worker to
 * activate. Unqueued frames, a recording or an unsaved edit mark the app busy
 * (src/lib/busy.ts), and then the operator gets an "Update ready" control
 * instead of a reload. The offline queue lives in IndexedDB and survives any
 * reload.
 */
export function DeploymentWatcher() {
  const { pathname, search, hash } = useLocation();
  const monitor = useRef<VersionMonitor | null>(null);
  const firstRoute = useRef(true);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => installBrowserPreloadRecovery(), []);

  useEffect(() => {
    if (!watcherEnabled()) return;
    const instance = createVersionMonitor({
      currentVersion: APP_VERSION,
      fetchVersion: () => fetchDeployedVersion(),
      isBusy: () => isBusy() || hasActivity(),
      isHidden: () => document.visibilityState === 'hidden',
      reload: () => window.location.reload(),
      navigate: (href) => window.location.assign(href),
      onUpdateReadyChange: setUpdateReady,
      beforeCheck: updateServiceWorker,
      beforeReload: () => waitForNewWorker(),
    });
    monitor.current = instance;
    const detach = attachVersionTriggers(instance, {
      window,
      document,
      setInterval: (handler, ms) => window.setInterval(handler, ms),
      clearInterval: (id) => window.clearInterval(id as number),
    });
    const unsubscribe = onBusyChange(() => instance.busyChanged());
    const untrack = installActivityTracking(() => instance.busyChanged());
    return () => {
      unsubscribe();
      untrack();
      detach();
      monitor.current = null;
    };
  }, []);

  useEffect(() => {
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    routeLeft();
    monitor.current?.routeChanged(window.location.href);
  }, [pathname, search, hash]);

  if (!updateReady) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)]">
      <div
        role="status"
        className="pointer-events-auto flex items-center gap-3 rounded-card border border-border-strong bg-surface py-1.5 pl-4 pr-1.5 text-sm text-surface-foreground shadow-lg"
      >
        <span>Update ready</span>
        <Button onClick={() => void monitor.current?.reloadNow()}>Reload</Button>
      </div>
    </div>
  );
}
